// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {Limits} from '@app/features/app/utils/UserLimits';
import {PremiumModal} from '@app/features/premium/components/modals/PremiumModal';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import {modal, push} from '@app/features/ui/commands/ModalCommands';
import type {User} from '@app/features/user/models/User';
import {MAX_BOOKMARKS_PREMIUM} from '@app/vendor/constants/LimitConstants';
import {plural} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

interface MaxBookmarksModalProps {
	user: User;
}
export const MaxBookmarksModal = observer(({user}: MaxBookmarksModalProps) => {
	const {t} = useLingui();
	const showPremium = shouldShowPremiumFeatures();
	const maxBookmarks = user.maxBookmarks;
	const premiumBookmarks = Limits.getPremiumValue('max_bookmarks', MAX_BOOKMARKS_PREMIUM);
	const canUpgradeBookmarks = maxBookmarks < premiumBookmarks;
	const bookmarksText = plural(
		{count: maxBookmarks},
		{
			one: '# bookmark',
			other: '# bookmarks',
		},
	);
	if (!showPremium) {
		return (
			<ConfirmModal
				title={t`Bookmark Limit Reached`}
				description={t`You've reached the maximum number of bookmarks (${bookmarksText}). This limit is configured by your instance administrator. Please remove some bookmarks before adding new ones.`}
				primaryText={t`Understood`}
				onPrimary={() => {}}
				secondaryText={false}
				hideCloseButton
			/>
		);
	}
	if (!canUpgradeBookmarks) {
		return (
			<ConfirmModal
				title={t`Bookmark Limit Reached`}
				description={t`You've reached the maximum number of bookmarks (${bookmarksText}). Please remove some bookmarks before adding new ones.`}
				primaryText={t`Understood`}
				onPrimary={() => {}}
				secondaryText={false}
				hideCloseButton
			/>
		);
	}
	const premiumBookmarksText = plural(
		{count: premiumBookmarks},
		{
			one: '# bookmark',
			other: '# bookmarks',
		},
	);
	return (
		<ConfirmModal
			title={t`Bookmark Limit Reached`}
			description={t`You've reached the maximum number of bookmarks for free users (${bookmarksText}). Upgrade to ${PREMIUM_PRODUCT_NAME} to increase your limit to ${premiumBookmarksText}, or remove some bookmarks to add new ones.`}
			primaryText={t`Upgrade to ${PREMIUM_PRODUCT_NAME}`}
			primaryVariant="primary"
			onPrimary={() => {
				window.setTimeout(() => {
					push(modal(() => <PremiumModal />));
				}, 0);
			}}
			secondaryText={t`Dismiss`}
		/>
	);
});
